/**
 * /living-documentation — SEO landing page targeting the "living documentation"
 * / "documentation drift" / "keep documentation up to date" / "self-updating
 * documentation" cluster. Audience: engineers, technical writers, PMs.
 *
 * Differentiation angle: living documentation is docs that stay in sync with the
 * system. The failure is documentation drift — docs written once and never
 * updated, wrong within weeks. Draftlize derives docs from a card graph; each
 * doc carries a source snapshot of the cards it was built from, and a background
 * detector flags the doc stale when an upstream card changes. This is literally
 * Draftlize's documents / stale_docs mechanic.
 *
 * English-only + inline (no i18n). Chinese variant in LivingDocumentationZh.tsx.
 */
import { MarketingShell } from './MarketingShell';

export function LivingDocumentation() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/living-documentation"
      documentTitle="Living Documentation That Doesn't Drift · Draftlize"
      documentDescription="Living documentation stays in sync with the system it describes. Most docs drift within weeks. Draftlize derives docs from a card graph and auto-flags them stale the moment an upstream decision changes."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Living documentation</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1.02, margin: '12px 0 24px' }}>
            Living documentation<br/>
            <em>that outlives the sprint.</em>
          </h1>
          <p className="lede">
            Living documentation is documentation that stays in sync with the system it describes, instead of freezing the day it was written. The enemy is <em>documentation drift</em>: a doc is accurate on publish, the product moves, and within weeks it is quietly, confidently wrong — and the reader cannot tell fresh from stale. The usual fixes — a review checklist, a docs sprint, more discipline — lose to shipping every time. Draftlize takes a different route: docs are <em>derived</em> from a card graph, each carries a snapshot of the cards it was built from, and when an upstream decision changes, the doc flags itself stale.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">A static doc vs <em>a living one.</em></h2>
          </div>
          <p>Every doc is accurate the day it ships. The question is what happens on day ninety — whether it can tell you it went out of date, or just keeps reading as truth.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Wiki / Confluence / markdown</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Where the doc comes from</td><td className="ours yes">Derived from source cards</td><td className="partial">Typed by hand, then detached</td></tr>
            <tr><td>A source decision changes</td><td className="ours yes">The doc auto-flags stale</td><td className="no">The doc silently goes wrong</td></tr>
            <tr><td>Can you trust it at a glance?</td><td className="ours yes">Stale is marked, not guessed</td><td className="no">You re-verify against the code</td></tr>
            <tr><td>Can an AI agent rely on it?</td><td className="ours yes">Reads current cards via MCP</td><td className="partial">Reads whatever text is there</td></tr>
            <tr><td>Who keeps it current</td><td className="ours yes">The substrate tracks drift</td><td className="no">A docs sprint that never comes</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Why documentation <em>drifts.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Written once, maintained never</h3>
            <p>A doc is perfect on the day it is written and a chore to update forever after. Maintenance competes with shipping and loses, so the doc freezes while the system keeps moving — and the gap widens every release.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">No link from the doc to the decision under it</h3>
            <p>A page describing how billing works has no connection to the <a href="/decision-log" style={{ color: 'var(--amber)' }}>decision</a> that set the billing rules. Change the rule and nothing tells the page it is now fiction. The dependency exists in reality but not in the tooling.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">The reader cannot tell fresh from stale</h3>
            <p>The worst part of drift is invisibility. A stale doc looks exactly like a current one, so the reader trusts it — and a doc that is confidently wrong is more expensive than no doc at all.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">How Draftlize makes docs <em>stay alive.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Docs are derived from cards</h3>
            <p>A design doc, a <a href="/technical-documentation-template" style={{ color: 'var(--amber)' }}>technical doc</a>, or a spec is generated from the underlying cards — the decisions, requirements, and rationale — rather than typed as freestanding prose. The doc has a real source, not just an author.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Each doc carries a source snapshot</h3>
            <p>When a doc is built, it records which cards it came from and when. That snapshot is what lets the system compare the doc against the current state of those cards — the link between doc and truth is stored, not remembered.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Change a card, the doc flags stale</h3>
            <p>A background detector watches the source cards. Change one an <a href="/architecture-decision-record" style={{ color: 'var(--amber)' }}>ADR</a> or spec depended on, and every doc derived from it flags stale — so the doc tells you it needs a look instead of waiting to be caught wrong.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          The problem was never writing the docs. It is that a doc cannot tell when it stopped being true.
        </blockquote>
        <cite>Derive the doc from the decisions. When they move, the doc says so.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Common questions.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: 'What is living documentation?', a: 'Living documentation is documentation that stays in sync with the system it describes, rather than freezing at the moment it was written. The term comes from the BDD world, where docs are generated from executable specifications. The broader idea is that documentation should be derived from a live source, so it updates or flags itself when the source changes.' },
            { q: 'What is documentation drift?', a: 'Documentation drift is the gap that opens between a document and the reality it describes as the system evolves and the doc does not. It is dangerous because a drifted doc looks identical to a current one, so readers keep trusting it — a confidently wrong doc costs more than no doc at all.' },
            { q: 'How do you keep documentation up to date?', a: 'The durable fix is to stop maintaining docs by hand and derive them from a single source, then flag them when that source changes. Draftlize generates docs from a card graph and records a snapshot of the source cards, so a background detector can flag a doc stale the moment an upstream decision moves.' },
            { q: 'How is this different from docs-as-code?', a: 'Docs-as-code keeps documentation in version control next to the code so it ships through the same pipeline — a real improvement, but the doc is still prose someone has to remember to update. Living documentation on a card graph adds the missing link: the doc knows which decisions it depends on, so it can flag itself stale instead of relying on a human to notice.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Documentation that flags its own drift <em>— free with $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>New accounts get $5 free</span>
          <span>Pay only for what you use</span>
          <span>Balance never expires</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Keep your decisions and specs as cards, derive your docs from them, and let each doc flag stale when a source decision moves.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
